import React from 'react';

import { TabItem, Tabs } from 'flowbite-react';
import * as Yup from 'yup';
import { useMutation } from '@tanstack/react-query';

import CrudForm from '../../../Form/CrudForm';
import Tradelines from './Tradelines';

import { YES_NO_OPTIONS } from '../../../../constants/formOptions';
import BorrowerService from '../../../../services/BorrowerService';
import { useUiStore } from '../../../../store';

const fields = [
  {
    label: 'Type',
    name: 'type',
    type: 'select',
    placeholder: 'Select a type',
    options: [
      { label: 'Soft', value: 'Soft' },
      { label: 'Hard', value: 'Hard' },
    ],
    validation: Yup.string().required('This field is required'),
  },
  {
    label: 'Date',
    name: 'date',
    type: 'date',
    placeholder: 'Select a date',
    validation: Yup.date(),
  },
  {
    label: 'Transunion',
    name: 'transunionScore',
    type: 'number',
    placeholder: 'Enter Transunion score',
    validation: Yup.number()
      .integer('This value must be a whole number')
      .min(0, 'This value must be between 0 and 999')
      .max(999, 'This value must be between 0 and 999'),
  },
  {
    label: 'Experian',
    name: 'experianScore',
    type: 'number',
    placeholder: 'Enter Experian score',
    validation: Yup.number()
      .integer('This value must be a whole number')
      .min(0, 'This value must be between 0 and 999')
      .max(999, 'This value must be between 0 and 999'),
  },
  {
    label: 'Equifax',
    name: 'equifaxScore',
    type: 'number',
    placeholder: 'Enter Equifax score',
    validation: Yup.number()
      .integer('This value must be a whole number')
      .min(0, 'This value must be between 0 and 999')
      .max(999, 'This value must be between 0 and 999'),
  },
  {
    label: 'Bankruptcy',
    name: 'bankruptcy',
    type: 'select',
    placeholder: 'Please select',
    options: YES_NO_OPTIONS,
    validation: Yup.bool(),
  },
  {
    label: 'Foreclosure',
    name: 'foreclosure',
    type: 'select',
    placeholder: 'Please select',
    options: YES_NO_OPTIONS,
    validation: Yup.bool(),
  },
  {
    label: 'Judgements',
    name: 'judgements',
    type: 'select',
    placeholder: 'Please select',
    options: YES_NO_OPTIONS,
    validation: Yup.bool(),
  },
];

export default function CreditReports({ borrowers, refetchBorrowers }) {
  const notifySuccess = useUiStore((state) => state.notifySuccess);
  const notifyApiError = useUiStore((state) => state.notifyApiError);

  const { mutate: createOrUpdateBorrowerCreditReport } = useMutation({
    mutationFn: ({ borrowerId, formValues }) =>
      BorrowerService.createOrUpdateBorrowerCreditReport({
        formValues,
        id: borrowerId,
      }),
    onSuccess: () => {
      notifySuccess('Success');
      refetchBorrowers();
    },
    onError: (error) => {
      console.log('Error creating/updating credit report:', error);
      notifyApiError(error);
    },
  });

  return (
    <div className="pb-10">
      <div className="h-full">
        <section className="bg-white dark:bg-gray-900">
          <div className="pt-8 px-4 md:px-12 mx-auto max-w-screen-xl lg:pt-16">
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-2">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Credit Reports
              </h2>
            </div>
            <Tabs>
              {borrowers?.map((borrower, index) => (
                <TabItem
                  key={index}
                  title={`${borrower.firstName} ${borrower.lastName}`}
                >
                  <div className="mb-6">
                    <CrudForm
                      data={borrower.CreditReport}
                      fields={fields}
                      submitButtonLabel="Submit"
                      updateHandler={(formValues) =>
                        createOrUpdateBorrowerCreditReport({
                          borrowerId: borrower.id,
                          formValues,
                        })
                      }
                      createHandler={(formValues) =>
                        createOrUpdateBorrowerCreditReport({
                          borrowerId: borrower.id,
                          formValues,
                        })
                      }
                    />
                  </div>
                  {borrower.CreditReport && (
                    <Tradelines
                      creditReport={borrower.CreditReport}
                      refetchBorrowers={refetchBorrowers}
                    />
                  )}
                </TabItem>
              ))}
            </Tabs>
          </div>
        </section>
      </div>
    </div>
  );
}
